import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { JwtService } from '@shared/services/jwt.service';
import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap } from 'rxjs';
import { IIsResetPasswordPossibleResponse, RequestResetPasswordDTO as RequestResetPasswordDto } from '../models';
import { IAuthenticationMethodResponse } from './login-page/models/IAuthenticationMethodResponse';
import { ILoginResponse } from './login-page/models/ILoginResponse';


@Injectable()
export class LoginService {

    constructor(
        private _jwt: JwtService,
        private _snackbar: MatSnackBar,
        private _httpClient: HttpClient,
    ) {
    }

    getAuthenticationMethod(userName: string): Observable<IAuthenticationMethodResponse> {
        return this._httpClient.get<IAuthenticationMethodResponse>(
            '/api/auth/authentication-method',
            { params: { userName } },
        );
    }

    login(username: string, password: string): Observable<{ redirectUrl: string }> {
        return this._httpClient.post<ILoginResponse>(
            '/api/auth/login',
            { username: username, password: password },
        ).pipe(
            map((response) => {
                sessionStorage.clear();
                this._jwt.token = response.jwt;
                sessionStorage.setItem('HACK_FirstAfterLogin', 'true');
                let redirectPath = response.route || '/';
                if (!redirectPath.startsWith('/')) {
                    redirectPath = `/${redirectPath}`;
                }
                return { redirectUrl: `${redirectPath}` };
            }),
        );
    }


    requestResetPassword(username: string): Observable<RequestResetPasswordDto> {
        return this._httpClient.post<RequestResetPasswordDto>('/api/auth/request-reset-password', {
            username: username,
        }).pipe(
            mergeMap((response: RequestResetPasswordDto) => {
                if (response && response.success) {
                    return of(response);
                }
                // eslint-disable-next-line rxjs-x/throw-error
                return throwError(() => response);
            }));
    }

    isResetPasswordPossible(resetPasswordKey: string): Observable<IIsResetPasswordPossibleResponse> {
        return this._httpClient.post<IIsResetPasswordPossibleResponse>('/api/auth/reset-password-check', {
            resetPasswordKey: resetPasswordKey,
        }).pipe(
            mergeMap((response) => {
                if (response) {
                    return of(response);
                }
                // eslint-disable-next-line rxjs-x/throw-error
                return throwError(() => response);
            }));
    }

    resetPassword(newPassword: string, resetPasswordKey: string): Observable<boolean> {
        return this._httpClient
            .post('/api/auth/reset-password', {
                newPassword: newPassword,
                resetPasswordKey: resetPasswordKey,
            }).pipe(
                mergeMap((response: unknown) => {
                    if (response && typeof response === 'boolean') {
                        return of(response);
                    }
                    return throwError(() => response);
                }));
    }

    setupPassword(newPassword: string, resetPasswordKey: string): Observable<boolean> {
        return this._httpClient
            .post('/api/auth/setup-password', {
                newPassword: newPassword,
                resetPasswordKey: resetPasswordKey,
            }).pipe(
                mergeMap((response: unknown) => {
                    if (response && typeof response === 'boolean') {
                        return of(response);
                    }
                    return throwError(() => response);
                }));
    }

    snackbar(message: string, action?: string, config?: MatSnackBarConfig): MatSnackBarRef<SimpleSnackBar> {
        if (config == undefined) {
            config = { duration: 3000 };
        }
        action = action || 'OK';
        return this._snackbar.open(message, action, config);
    }
}
